import { useState, useEffect } from 'react';

/**
 * Vue/onglet interne d'un module qui SURVIT à l'actualisation de la page.
 *
 * Le module courant survit déjà au refresh (App.js `currentPage` + URL) ; ce
 * hook fait la même chose pour la vue INTERNE d'un module (ex. Prévention →
 * Planification) : `useState` adossé à sessionStorage.
 *
 * - sessionStorage (PAS localStorage) : survit au refresh de l'onglet, meurt
 *   avec l'onglet → un nouvel onglet ou une nouvelle session repart au défaut.
 * - `clearPersistedViews()` est appelé à la CONNEXION (Login) : une nouvelle
 *   authentification repart toujours sur le tableau de bord de chaque module.
 * - Valeur stockée inconnue/périmée (vue renommée, RBAC changé…) → repli sur
 *   le défaut, jamais de crash : passer `valeursValides` quand la liste est
 *   fermée.
 *
 * @param {string} cle           identifiant stable du module (ex. 'prevention')
 * @param {string} defaut        vue par défaut (ex. 'dashboard')
 * @param {string[]} [valeursValides] liste blanche optionnelle des vues restaurables
 * @returns {[string, Function]} même contrat que useState
 */
const PREFIX = 'pfm_view_';

/**
 * Lecture seule, sans React — pour les initializers multi-sources (ex.
 * GestionActifs où l'ordre est URL ?tab= > localStorage one-shot > vue
 * mémorisée > défaut). Retourne la vue mémorisée ou null.
 */
export function readPersistedView(cle, valeursValides) {
  try {
    const sauvee = sessionStorage.getItem(`${PREFIX}${cle}`);
    if (!sauvee) return null;
    if (valeursValides && !valeursValides.includes(sauvee)) return null;
    return sauvee;
  } catch {
    return null;
  }
}

/** Écriture directe, sans React — compagnon de readPersistedView. */
export function writePersistedView(cle, vue) {
  try { sessionStorage.setItem(`${PREFIX}${cle}`, vue); } catch { /* indisponible */ }
}

export function usePersistedView(cle, defaut, valeursValides) {
  const storageKey = `${PREFIX}${cle}`;
  const [vue, setVue] = useState(() => {
    try {
      const sauvee = sessionStorage.getItem(storageKey);
      if (!sauvee) return defaut;
      if (valeursValides && !valeursValides.includes(sauvee)) return defaut;
      return sauvee;
    } catch {
      return defaut; // sessionStorage indisponible (navigation privée stricte…)
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, vue);
    } catch { /* stockage indisponible : la vue vit en mémoire seulement */ }
  }, [storageKey, vue]);

  return [vue, setVue];
}

/**
 * Purge toutes les vues mémorisées. Appelé à la connexion pour garantir
 * « login → tableau de bord » partout, quel que soit l'état de l'onglet.
 */
export function clearPersistedViews() {
  try {
    const aSupprimer = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(PREFIX)) aSupprimer.push(k);
    }
    aSupprimer.forEach((k) => sessionStorage.removeItem(k));
  } catch { /* stockage indisponible : rien à purger */ }
}

export default usePersistedView;
